import { faPlus, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import useNotification from 'hooks/useNotification';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BusinessScheduleService from 'services/BusinessSchedule.service';

const DAY_ORDER = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY'
];

const DAY_LABELS = {
  SUNDAY: 'Domingo',
  MONDAY: 'Segunda-feira',
  TUESDAY: 'Terça-feira',
  WEDNESDAY: 'Quarta-feira',
  THURSDAY: 'Quinta-feira',
  FRIDAY: 'Sexta-feira',
  SATURDAY: 'Sábado'
};

const buildDefaultDays = () =>
  DAY_ORDER.map(day => ({
    day,
    enabled: day !== 'SUNDAY' && day !== 'SATURDAY',
    shifts:
      day !== 'SUNDAY' && day !== 'SATURDAY'
        ? [{ startTime: '08:00', endTime: '18:00' }]
        : []
  }));

const timeToMinutes = time => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const shiftsOverlap = (a, b) => {
  const aStart = timeToMinutes(a.startTime);
  const aEnd = timeToMinutes(a.endTime);
  const bStart = timeToMinutes(b.startTime);
  const bEnd = timeToMinutes(b.endTime);
  return aStart < bEnd && bStart < aEnd;
};

const validate = (name, days) => {
  const errors = [];

  if (!name.trim()) {
    errors.push('O nome é obrigatório.');
  }

  for (const dayObj of days) {
    if (!dayObj.enabled) continue;

    if (dayObj.shifts.length === 0) {
      errors.push(`${DAY_LABELS[dayObj.day]}: adicione ao menos um turno.`);
      continue;
    }

    for (const shift of dayObj.shifts) {
      if (!shift.startTime || !shift.endTime) {
        errors.push(
          `${DAY_LABELS[dayObj.day]}: preencha os horários de todos os turnos.`
        );
        break;
      }
      if (timeToMinutes(shift.startTime) >= timeToMinutes(shift.endTime)) {
        errors.push(
          `${
            DAY_LABELS[dayObj.day]
          }: o horário de início deve ser anterior ao de fim.`
        );
        break;
      }
    }

    for (let i = 0; i < dayObj.shifts.length; i++) {
      for (let j = i + 1; j < dayObj.shifts.length; j++) {
        if (shiftsOverlap(dayObj.shifts[i], dayObj.shifts[j])) {
          errors.push(
            `${DAY_LABELS[dayObj.day]}: os turnos não podem se sobrepor.`
          );
        }
      }
    }
  }

  return errors;
};

const BusinessScheduleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [days, setDays] = useState(buildDefaultDays());

  useEffect(() => {
    if (isEdit) loadData();
  }, [id]);

  const loadData = async () => {
    setIsLoading(true);
    const response = await BusinessScheduleService.findById(id);
    if (response && !response.status) {
      setName(response.name || '');
      setDescription(response.description || '');
      const sorted = DAY_ORDER.map(day => {
        const found = response.days?.find(d => d.day === day);
        return found
          ? {
              day: found.day,
              enabled: found.enabled,
              shifts: (found.shifts || []).map(s => ({
                startTime: s.startTime?.slice(0, 5) || '',
                endTime: s.endTime?.slice(0, 5) || ''
              }))
            }
          : { day, enabled: false, shifts: [] };
      });
      setDays(sorted);
    }
    setIsLoading(false);
  };

  const setDayEnabled = (dayIndex, enabled) => {
    setDays(prev =>
      prev.map((d, i) =>
        i === dayIndex
          ? {
              ...d,
              enabled,
              shifts:
                enabled && d.shifts.length === 0
                  ? [{ startTime: '08:00', endTime: '18:00' }]
                  : d.shifts
            }
          : d
      )
    );
  };

  const addShift = dayIndex => {
    setDays(prev =>
      prev.map((d, i) =>
        i === dayIndex
          ? { ...d, shifts: [...d.shifts, { startTime: '', endTime: '' }] }
          : d
      )
    );
  };

  const removeShift = (dayIndex, shiftIndex) => {
    setDays(prev =>
      prev.map((d, i) =>
        i === dayIndex
          ? { ...d, shifts: d.shifts.filter((_, si) => si !== shiftIndex) }
          : d
      )
    );
  };

  const copyToWeekdays = dayIndex => {
    const source = days[dayIndex];
    const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
    setDays(prev =>
      prev.map(d =>
        weekdays.includes(d.day)
          ? { ...d, enabled: true, shifts: source.shifts.map(s => ({ ...s })) }
          : d
      )
    );
  };

  const setShiftField = (dayIndex, shiftIndex, field, value) => {
    setDays(prev =>
      prev.map((d, i) =>
        i === dayIndex
          ? {
              ...d,
              shifts: d.shifts.map((s, si) =>
                si === shiftIndex ? { ...s, [field]: value } : s
              )
            }
          : d
      )
    );
  };

  const handleSubmit = async () => {
    const errors = validate(name, days);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);

    const payload = {
      name: name.trim(),
      description: description.trim(),
      days: days.map(d => ({
        day: d.day,
        enabled: d.enabled,
        shifts: d.enabled
          ? d.shifts.map(s => ({ startTime: s.startTime, endTime: s.endTime }))
          : []
      }))
    };

    const response = isEdit
      ? await BusinessScheduleService.edit(id, payload)
      : await BusinessScheduleService.create(payload);

    if (response.status && response.status >= 400) {
      const body = await response.json().catch(() => ({}));
      showErrorNotification(body.userMessage || 'Ocorreu um erro ao salvar o horário comercial.');
    } else {
      showSuccessNotification(
        isEdit
          ? 'Horário comercial atualizado com sucesso'
          : 'Horário comercial criado com sucesso'
      );
      navigate('/settings/business-schedules');
    }
  };

  if (isLoading) {
    return (
      <FalconComponentCard>
        <FalconComponentCard.Body>
          <Skeleton count={10} height={44} baseColor="#5E6E82" />
        </FalconComponentCard.Body>
      </FalconComponentCard>
    );
  }

  return (
    <>
      <FalconComponentCard>
        <Flex direction="row" alignItems="center">
          <IconButton
            onClick={() => navigate('/settings/business-schedules')}
            variant="falcon-default"
            size="sm"
            icon="arrow-left"
            className="ms-3"
          />
          <FalconComponentCard.Header
            title={
              isEdit ? 'Editar horário comercial' : 'Novo horário comercial'
            }
          />
        </Flex>

        <FalconComponentCard.Body>
          {validationErrors.length > 0 && (
            <div className="alert alert-danger" role="alert">
              <ul className="mb-0 ps-3">
                {validationErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              Nome <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Nome do horário"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Descrição (opcional)"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </Form.Group>

          <Form.Label className="fw-semi-bold">Dias da semana</Form.Label>
          {days.map((dayObj, dayIndex) => (
            <Card className="mb-2" key={dayObj.day}>
              <Card.Body className="py-2">
                <Flex alignItems="start" justifyContent="between">
                  <div style={{ minWidth: 160 }}>
                    <Form.Check
                      type="switch"
                      id={`switch-${dayObj.day}`}
                      label={
                        <span className="fw-semi-bold">
                          {DAY_LABELS[dayObj.day]}
                        </span>
                      }
                      checked={dayObj.enabled}
                      onChange={e => setDayEnabled(dayIndex, e.target.checked)}
                    />
                  </div>

                  {dayObj.enabled && (
                    <div className="flex-1 ms-3">
                      {dayObj.shifts.map((shift, shiftIndex) => (
                        <Row
                          className="mb-2 align-items-center"
                          key={shiftIndex}
                        >
                          <Col xs="auto">
                            <Form.Label className="mb-0 text-600 fs-10">
                              Início
                            </Form.Label>
                            <Form.Control
                              type="time"
                              size="sm"
                              value={shift.startTime}
                              onChange={e =>
                                setShiftField(
                                  dayIndex,
                                  shiftIndex,
                                  'startTime',
                                  e.target.value
                                )
                              }
                            />
                          </Col>
                          <Col xs="auto">
                            <Form.Label className="mb-0 text-600 fs-10">
                              Fim
                            </Form.Label>
                            <Form.Control
                              type="time"
                              size="sm"
                              value={shift.endTime}
                              onChange={e =>
                                setShiftField(
                                  dayIndex,
                                  shiftIndex,
                                  'endTime',
                                  e.target.value
                                )
                              }
                            />
                          </Col>
                          <Col xs="auto" className="mt-3">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="border-0 p-1"
                              onClick={() => removeShift(dayIndex, shiftIndex)}
                              title="Remover turno"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} size="sm" />
                            </Button>
                          </Col>
                        </Row>
                      ))}
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 mt-1"
                        onClick={() => addShift(dayIndex)}
                      >
                        <FontAwesomeIcon
                          icon={faPlus}
                          size="sm"
                          className="me-1"
                        />
                        Adicionar turno
                      </Button>
                    </div>
                  )}

                  {dayObj.enabled && (
                    <div className="ms-auto ps-3 d-flex align-items-start pt-1">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-nowrap fs-11 py-1 px-2"
                        onClick={() => copyToWeekdays(dayIndex)}
                        title="Copia os turnos deste dia para todos os dias úteis (seg-sex)"
                      >
                        Copiar para todos (seg-sex)
                      </Button>
                    </div>
                  )}
                </Flex>
              </Card.Body>
            </Card>
          ))}

          <Flex justifyContent="end" className="mt-4 gap-2">
            <Link
              to="/settings/business-schedules"
              className="btn btn-default me-2"
            >
              Cancelar
            </Link>
            <Button variant="primary" onClick={handleSubmit}>
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Salvar
            </Button>
          </Flex>
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default BusinessScheduleForm;
